/**
 * ValetChat.jsx
 *
 * The conversational interface for Music Valet AI.
 * Supports text and voice input for natural music requests.
 * Works in ANY language - auto-detects or user-selectable.
 *
 * "Make this playlist more upbeat"
 * "Nunca quiero escuchar smooth jazz"
 * "Construis-moi quelque chose pour un dîner"
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { refreshAuthSession, useAuth } from './AuthView';
import { ValetIcon, ValetIconCircle } from './icons/ValetIcon';
import { useMaestroStore } from '../store/maestroStore';
import { isIOSNativeApp } from '../utils/platform';

// =============================================================================
// FLOATING ACTION BUTTON - Opens the chat
// =============================================================================

export function ValetFAB({ onClick, hasUnread }) {
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const hasActiveTrack = !!currentTrack;
  const [bottomPx, setBottomPx] = useState(100);

  useEffect(() => {
    const compute = () => {
      try {
        const el = document.querySelector('.mini-player-container');
        if (!el) {
          setBottomPx(hasActiveTrack ? 180 : 100);
          return;
        }
        const rect = el.getBoundingClientRect();
        const h = Number(rect?.height || 0);
        setBottomPx(Math.floor(h + 24));
      } catch {
        setBottomPx(hasActiveTrack ? 180 : 100);
      }
    };

    compute();
    const timer = setTimeout(compute, 300);

    let ro = null;
    try {
      const el = document.querySelector('.mini-player-container');
      if (el && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => compute());
        ro.observe(el);
      }
    } catch {}

    window.addEventListener('resize', compute);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', compute);
      try { ro?.disconnect?.(); } catch {}
    };
  }, [hasActiveTrack]);

  return (
    <button
      onClick={onClick}
      className="fixed z-[70] hover:scale-110 transition-all flex items-center justify-center group"
      style={
        hasActiveTrack
          ? { left: '50%', transform: 'translateX(-50%)', bottom: `${bottomPx}px` }
          : { right: '20px', bottom: `${bottomPx}px` }
      }
      title="Chat with Ella"
    >
      <ValetIconCircle size={hasActiveTrack ? 56 : 44} />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-zinc-900" />
      )}
    </button>
  );
}

// =============================================================================
// ICONS
// =============================================================================

const MicIcon = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill={active ? 'currentColor' : 'none'} />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
  </svg>
);

import useMicrophone from '../hooks/useMicrophone';
import useEllaMicStart from '../hooks/useEllaMicStart';
import { VALET_LANGUAGES } from './valetChatLanguages';

export default function ValetChat({
  isOpen,
  onClose,
  currentPlaylist,
  currentTrack,
  onPlaylistAction,
}) {
  const auth = useAuth();
  const accessToken = auth?.accessToken || null;
  const authUser = auth?.user || null;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [language, setLanguage] = useState(''); // Auto-detect by default
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [toast, setToast] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionStorageKey = `maestro_valet_chat_session:${String(authUser?.id || 'guest')}`;
  const transcriptStorageKey = `maestro_valet_chat_transcript:${String(authUser?.id || 'guest')}`;

  const mic = useMicrophone({
    onResult: (transcript, confidence) => {
      setVoiceError(null);
      setInput(transcript);
      setTimeout(() => {
        if (transcript.trim()) {
          sendMessage(transcript, true, confidence);
          setInput('');
        }
      }, 300);
    },
    onError: (error) => {
      console.error('[ValetChat] Voice error:', error);
      const msg =
        error === 'not-allowed' || error === 'permission-denied'
          ? isIOSNativeApp()
            ? 'Microphone access is needed for Ella. Please allow microphone access when prompted.'
            : 'Microphone permission is blocked for this site. Enable it in your browser settings and reload.'
          : error === 'no-speech'
            ? 'No speech detected. Try again closer to the mic.'
            : error === 'audio-capture'
              ? 'No microphone available. Check the selected input device.'
              : error === 'network'
                ? 'Speech recognition requires network access in this browser.'
                : `Voice input error: ${String(error)}`;
      setVoiceError(msg);
    },
    language,
  });
  const isListening = mic.isListening;
  const voiceSupported = mic.isSupported;
  const startVoice = useCallback(() => {
    setVoiceError(null);
    mic.start();
  }, [mic]);
  const { requestStart: requestMicStart, dialog: micConsentDialog } = useEllaMicStart(startVoice);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Escape-to-close (desktop UX)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const buildTranscriptText = useCallback(() => {
    const header = `Maestro Valet Conversation\n${new Date().toISOString()}\n`;
    const body = (messages || [])
      .map((m) => {
        const who = m?.role === 'user' ? 'You' : 'Ella';
        const content = String(m?.content || '').trim();
        return `${who}: ${content}`;
      })
      .join('\n\n');
    return `${header}\n${body}\n`;
  }, [messages]);

  const saveTranscript = useCallback(async () => {
    const txt = buildTranscriptText();
    try {
      localStorage.setItem(transcriptStorageKey, txt);
    } catch {}

    // Prefer clipboard for quick sharing; also trigger a download as fallback.
    let copied = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(txt);
        copied = true;
      }
    } catch {}

    if (!copied) {
      try {
        const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `maestro-valet-${new Date().toISOString().slice(0, 19).replace(/[:]/g, '-')}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch {}
    }

    setToast(copied ? 'Conversation copied to clipboard' : 'Conversation saved');
  }, [buildTranscriptText, transcriptStorageKey]);

  const clearConversation = useCallback(() => {
    const ok = window.confirm('Clear this conversation with Ella?');
    if (!ok) return;
    setMessages([]);
    setInput('');
    setSessionId(null);
    try {
      localStorage.removeItem(sessionStorageKey);
      localStorage.removeItem(transcriptStorageKey);
    } catch {}
    setToast('Conversation cleared');
  }, [sessionStorageKey, transcriptStorageKey]);

  // Persist sessionId across messages/re-openings (per-user).
  useEffect(() => {
    if (!isOpen) return;
    if (sessionId) return;
    try {
      const stored = localStorage.getItem(sessionStorageKey);
      if (stored && stored !== 'null' && stored !== 'undefined') {
        setSessionId(stored);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionStorageKey]);

  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.setItem(sessionStorageKey, String(sessionId));
    } catch {}
  }, [sessionId, sessionStorageKey]);

  // Send message to valet API
  const sendMessage = async (text, wasVoice = false, voiceConfidence = null) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text.trim(), wasVoice };
    // Capture a stable view of recent conversation for the request payload.
    // This prevents "stateless guest" behavior where the server has no prior turns.
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setIsLoading(true);

    try {
      const doRequest = async (tokenOverride) => {
        const headers = { 'Content-Type': 'application/json' };
        const tok = tokenOverride || accessToken;
        if (tok) headers.Authorization = `Bearer ${tok}`;
        const response = await fetch(`${CLOUD_API_URL}/api/valet/chat`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message: text.trim(),
            sessionId,
            wasVoiceInput: wasVoice,
            voiceConfidence,
            context: {
              // Provide recent conversation so Valet can stay coherent even for guests
              // (or when DB persistence is unavailable).
              recentConversation: nextMessages
                .slice(-10)
                .map((m) => ({ role: m?.role, content: m?.content, wasVoice: !!m?.wasVoice })),
              currentPlaylist: currentPlaylist ? {
                id: currentPlaylist.id,
                name: currentPlaylist.name,
                trackCount: currentPlaylist.tracks?.length,
              } : null,
              currentTrack: currentTrack ? {
                title: currentTrack.title,
                artist: currentTrack.artist,
                album: currentTrack.album,
              } : null,
              timeOfDay: new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening',
            },
          }),
        });
        const data = await response.json().catch(() => null);
        return { response, data };
      };

      let { response, data } = await doRequest(null);

      // If token expired, refresh once and retry.
      if ((!response?.ok || response?.status === 401) && String(data?.error || '').toLowerCase().includes('token') && auth?.refreshToken) {
        try {
          const session = await refreshAuthSession();
          ({ response, data } = await doRequest(session?.access_token || null));
        } catch {
          // fall through to error rendering below
        }
      }

      if (data.success) {
        setSessionId(data.sessionId);
        setMessages(prev => [...prev, {
          role: 'valet',
          content: data.response,
          intent: data.intent,
          instructionStored: data.instructionStored,
        }]);

        // Handle actions
        if (data.intent?.action && data.intent.action !== 'none' && data.intent.confidence > 0.7) {
          handleValetAction(data.intent);
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'valet',
          content: data.error || "I'm having trouble right now. Please try again.",
          isError: true,
        }]);
      }
    } catch (err) {
      console.error('[ValetChat] Error:', err);
      setMessages(prev => [...prev, {
        role: 'valet',
        content: "Connection issue. Please try again.",
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle valet actions (playlist modifications, etc.)
  const handleValetAction = (intent) => {
    if (intent?.action === 'create_playlist' || intent?.action === 'generate_playlist') {
      // Navigate to Smart Playlists and pass the request via sessionStorage
      const transcript = intent?.parameters?.description || intent?.parameters?.name || messages.filter(m => m.role === 'user').pop()?.content || '';
      if (transcript) {
        sessionStorage.setItem('smart_playlist_request', transcript);
      }
      window.location.href = '/app/smart-playlists';
      return;
    }
    if (onPlaylistAction) {
      onPlaylistAction(intent);
    }
  };

  const renderValetContent = (content) => {
    const text = String(content || '');
    // Fallback formatting: detect inline "1. ... 2. ... 3. ..." lists and render cleanly.
    const re = /(^|[\s\n])(\d{1,2})\.\s+/g;
    const matches = Array.from(text.matchAll(re));
    if (matches.length < 2) {
      return <p className="text-sm leading-relaxed whitespace-pre-wrap">{text}</p>;
    }

    const first = matches[0];
    const prefix = text.slice(0, first.index).trim();
    const itemStarts = matches.map((m) => (m.index || 0) + String(m[0] || '').length);

    const items = itemStarts.map((start, i) => {
      const end = i + 1 < itemStarts.length ? (matches[i + 1].index || text.length) : text.length;
      return text.slice(start, end).trim();
    });

    return (
      <div className="text-sm leading-relaxed">
        {prefix ? <p className="whitespace-pre-wrap">{prefix}</p> : null}
        <ol className="mt-2 ml-5 list-decimal space-y-1 text-white/90">
          {items.map((it, i) => (
            <li key={i} className="text-white/90">
              {it}
            </li>
          ))}
        </ol>
      </div>
    );
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      sendMessage(input);
      setInput('');
    }
  };

  const toggleVoice = () => {
    if (isListening || mic.isPreparing) mic.stop();
    else requestMicStart();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4">
      {micConsentDialog}
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Chat Window */}
      <div className="relative w-full max-w-lg bg-zinc-900 rounded-2xl shadow-2xl border border-white/10 flex flex-col max-h-[80vh] overflow-hidden">
        {toast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-black/70 border border-white/10 text-xs text-white/80 backdrop-blur-md">
            {toast}
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ValetIconCircle size={40} />
            <div>
              <h3 className="font-medium text-white">Music Valet</h3>
              <p className="text-xs text-white/50">Your personal music concierge</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-white/70 flex items-center gap-1"
                title="Voice language"
              >
                {VALET_LANGUAGES.find(l => l.code === language)?.flag || '🌐'}
              </button>
              {showLanguageMenu && (
                <div className="absolute right-0 top-full mt-1 bg-zinc-800 border border-white/10 rounded-lg shadow-xl py-1 z-10 max-h-64 overflow-y-auto">
                  {VALET_LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLanguageMenu(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-white/10 flex items-center gap-2 ${
                        language === lang.code ? 'text-amber-400' : 'text-white/70'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={saveTranscript}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
              title="Save conversation"
              aria-label="Save conversation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            <button
              type="button"
              onClick={clearConversation}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
              title="Close (Esc)"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8 text-white/40">
              <div className="mx-auto mb-4">
                <ValetIconCircle size={64} />
              </div>
              <p className="text-sm">How can I help with your music today?</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {[
                  "Build me a playlist for focus",
                  "More upbeat please",
                  "Something like Miles Davis",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion);
                      sendMessage(suggestion);
                    }}
                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/60 hover:text-white transition"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-amber-500 text-black'
                    : msg.isError
                    ? 'bg-red-500/20 text-red-300'
                    : 'bg-white/10 text-white'
                }`}
              >
                {msg.role === 'user'
                  ? <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  : renderValetContent(msg.content)}
                {msg?.intent?.action === 'create_playlist' && msg?.intent?.parameters && (
                  <div className="mt-3 rounded-xl bg-black/30 border border-white/10 p-3">
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-white/45">
                      Playlist
                    </div>
                    <div className="mt-1 font-semibold text-white">
                      {msg.intent.parameters.name || msg.intent.parameters.playlist_name || 'New playlist'}
                    </div>
                    {msg.intent.parameters.description && (
                      <div className="mt-1 text-xs text-white/60">{msg.intent.parameters.description}</div>
                    )}
                    {Array.isArray(msg.intent.parameters.track_suggestions) && msg.intent.parameters.track_suggestions.length > 0 && (
                      <ol className="mt-3 ml-5 list-decimal space-y-1 text-xs text-white/80">
                        {msg.intent.parameters.track_suggestions.slice(0, 10).map((t, i) => {
                          const isObj = t && typeof t === 'object' && !Array.isArray(t);
                          const artist = isObj ? String(t.artist || '').trim() : '';
                          const title = isObj ? String(t.title || t.name || '').trim() : String(t || '').trim();
                          const line = artist ? `${artist} — ${title || 'Track'}` : (title || 'Track');
                          return (
                            <li key={i} className="text-white/85">
                              {line}
                            </li>
                          );
                        })}
                      </ol>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleValetAction(msg.intent)}
                        className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition"
                      >
                        Build (Maestro names)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const nm = prompt('Playlist name?');
                          if (!nm) return;
                          handleValetAction({
                            ...msg.intent,
                            parameters: { ...(msg.intent.parameters || {}), name: nm },
                          });
                        }}
                        className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs font-bold hover:bg-white/15 transition"
                      >
                        Build (I name it)
                      </button>
                    </div>
                  </div>
                )}
                {msg.wasVoice && (
                  <span className="text-xs opacity-60 mt-1 flex items-center gap-1">
                    <MicIcon active={false} /> Voice
                  </span>
                )}
                {msg.instructionStored && (
                  <span className="text-xs text-amber-300 mt-1 block">
                    ✓ I'll remember that
                  </span>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl bg-white/10 text-white/60">
                <div className="flex items-center gap-2">
                  <SpinnerIcon />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            {/* Voice Button */}
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleVoice}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                  isListening
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/20'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                <MicIcon active={isListening} />
              </button>
            )}

            {/* Text Input */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isListening ? 'Listening...' : 'Ask your valet anything...'}
              disabled={isListening}
              className="flex-1 bg-white/10 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-amber-500/50"
            />

            {/* Send Button */}
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="w-10 h-10 rounded-full bg-amber-500 text-black flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-400 transition"
            >
              <SendIcon />
            </button>
          </div>

          {isListening && (
            <p className="text-xs text-center mt-2 text-red-400 animate-pulse">
              🎤 Listening... speak now
            </p>
          )}
          {!isListening && voiceError && (
            <p className="text-xs text-center mt-2 text-red-300">
              {voiceError}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
